import { Button, Col, Descriptions, Row } from "antd";
import moment from "moment";
import { useCallback } from "react";
import { useMutation } from "react-query";
import { putOrderRepairOnSiteByOrderNo } from "../../../../apis/Order";
import useModals from "../../../../hooks/useModals";
import { OrderItem } from "../../../../model/OrderItem";
import ReservationDetail from "../../../../model/ReservationDetail";
import { HttpError } from "../../../../network/types";
import { alertError, alertSuccess } from "../../../../shared/utils";
import { ProductStatusType } from "../Expended/Products/ProductStatus";
import { ReservationOnsiteDateTimeChangeModal } from "../Modal/Onsite/DateTime";
import { OnsiteStatusTag, OnsiteStatusType } from "../Tags/OnsiteStatus";
import { AddressForm } from "./Address";
import { CardHeader, CardTitle, CustomCard } from "./Card.styled";
import { DescriptionTitle } from "./Description.styled";
import { ProductInfo } from "./ProductInfo";

interface OnSiteInfoFormProps {
  dataForm: ReservationDetail;
  isMaster?: boolean;
  onChange?: () => void;
}

export const OnSiteInfoForm: React.FC<OnSiteInfoFormProps> = ({
  dataForm,
  isMaster,
  onChange = () => void 0,
}) => {
  const { onSite } = dataForm;
  const { openModal, closeModal } = useModals();

  const parsingProductInfo = useCallback(
    (record: OrderItem[], type: ProductStatusType) => {
      const typeItems = record?.filter((item) => item.productType === type);

      if (type === "ENGINE_OIL") {
        return (
          typeItems?.reduce((a, b) => {
            const items = b.orderItemProductOptions || [];

            const arr = items.map((item) => ({
              ...item,
              orderItemStatus: b.orderItemStatus,
              orderItemReturnStatus: b.orderItemReturnStatus,
              orderItemName: b.orderItemName,
            }));

            return a.concat(...arr);
          }, [] as any) || []
        );
      }

      return typeItems;
    },
    [dataForm]
  );

  const { mutate: changeOnsiteMutate, isLoading: isOnsiteLoading } =
    useMutation(putOrderRepairOnSiteByOrderNo, {
      onSuccess: () => {
        alertSuccess("예약일시가 변경되었습니다.");
        closeModal();
        onChange();
      },
      onError: (err: HttpError) => {
        alertError(err.message);
      },
    });

  return (
    <CustomCard title={<CardTitle>출장교체정보</CardTitle>}>
      <Descriptions column={2} colon={false} labelStyle={{ width: 140 }}>
        <Descriptions.Item
          label={<DescriptionTitle>주문번호</DescriptionTitle>}
        >
          {dataForm?.repair.repairNo || "-"}
        </Descriptions.Item>
        <Descriptions.Item
          label={<DescriptionTitle>주문일시</DescriptionTitle>}
        >
          {dataForm?.order.orderedAt
            ? moment(dataForm?.order.orderedAt).format("YYYY-MM-DD HH:mm")
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item
          label={<DescriptionTitle>출장예약일시</DescriptionTitle>}
        >
          <Row gutter={4} align="middle">
            <Col>
              {onSite?.onSitedAt
                ? moment(onSite?.onSitedAt).format("YYYY-MM-DD HH:mm")
                : "-"}
            </Col>
            <Col>
              <Button
                ghost
                size="small"
                type="default"
                shape="round"
                style={{
                  fontSize: 13,
                  height: 18,
                  display: "inline-flex",
                  alignItems: "center",
                }}
                disabled={
                  onSite &&
                  !["PENDING", "COMPLETED_PAYMENT", "APPROVED"].includes(
                    onSite.onSiteStatus
                  )
                }
                onClick={() => {
                  openModal(
                    <ReservationOnsiteDateTimeChangeModal
                      defaultValue={onSite?.onSitedAt}
                      onClose={(value) => {
                        if (value) {
                          changeOnsiteMutate({
                            orderNo: dataForm.repair.repairNo,
                            onSitedAt: value?.toISOString(),
                          });
                        }
                      }}
                    />
                  );
                }}
              >
                변경
              </Button>
            </Col>
          </Row>
        </Descriptions.Item>
        <Descriptions.Item
          label={<DescriptionTitle>출장예약상태</DescriptionTitle>}
        >
          <OnsiteStatusTag value={onSite?.onSiteStatus as OnsiteStatusType} />
        </Descriptions.Item>
        <Descriptions.Item
          label={<DescriptionTitle>출장예약주소</DescriptionTitle>}
          span={2}
        >
          <AddressForm
            detail={onSite?.detail}
            street={onSite?.street}
            zibun={onSite?.zibun}
          />
        </Descriptions.Item>
        <Descriptions.Item
          label={<DescriptionTitle>출장요청사항</DescriptionTitle>}
          span={2}
        >
          {onSite?.onSiteMemo || "-"}
        </Descriptions.Item>
        <Descriptions.Item
          label={<DescriptionTitle>상품정보</DescriptionTitle>}
          span={2}
        >
          <Row
            justify="space-between"
            gutter={[0, 24]}
            style={{ width: "100%" }}
          >
            {parsingProductInfo(
              dataForm?.repair.orderItems as OrderItem[],
              "TIRE"
            )?.length > 0 && (
              <Col span={24}>
                <Descriptions
                  layout="vertical"
                  colon={false}
                  column={2}
                  style={{ width: "100%" }}
                  contentStyle={{ width: "100%", display: "block" }}
                >
                  <Descriptions.Item
                    label={<CardHeader>타이어 장착</CardHeader>}
                    span={2}
                  >
                    <ProductInfo
                      dataSource={parsingProductInfo(
                        dataForm?.repair.orderItems as OrderItem[],
                        "TIRE"
                      )}
                      type="TIRE"
                    />
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            )}
            {parsingProductInfo(
              dataForm?.repair.orderItems as OrderItem[],
              "ENGINE_OIL"
            )?.length > 0 && (
              <Col span={24}>
                <Descriptions
                  layout="vertical"
                  colon={false}
                  column={2}
                  style={{ width: "100%" }}
                  contentStyle={{ width: "100%", display: "block" }}
                >
                  <Descriptions.Item
                    label={
                      <CardHeader>
                        엔진오일교환
                        {` | ${
                          parsingProductInfo(
                            dataForm?.repair.orderItems as OrderItem[],
                            "ENGINE_OIL"
                          )[0].orderItemName
                        }`}
                      </CardHeader>
                    }
                    span={2}
                  >
                    <ProductInfo
                      dataSource={parsingProductInfo(
                        dataForm?.repair.orderItems as OrderItem[],
                        "ENGINE_OIL"
                      )}
                      type="ENGINE_OIL"
                    />
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            )}
          </Row>
        </Descriptions.Item>
      </Descriptions>
    </CustomCard>
  );
};
